/**
 * IraContributionPhaseoutsPage — Reference Data · IRA Contribution Phaseouts.
 *
 * MAGI phaseout brackets per (Tax Year × Plan Code × Filing Status). Drives
 * Traditional-IRA deductibility caps and Roth-IRA eligibility caps. IRS-
 * sensitive — numbers come straight from IRS Publication 590-A and are
 * updated each fall when the IRS publishes the next year's inflation-adjusted
 * amounts. Rows are grouped by tax year; a "Roll forward" action clones the
 * latest year's (plan × filing-status) grid into next year with blank dollar
 * amounts for admin entry.
 *
 * Contract (verified against the backend 2026-06-16 — entity / DTO / service /
 * read route all agree on the wire field names; no drift):
 *   - Entity:  PCBJxChangeApi/Data/Entities/Config/Codes/IraContributionPhaseout.cs
 *              composite PK (TaxYear, PlanCode, FilingStatus); has IsActive.
 *   - Read:    GET /v3/config/ira-contribution-phaseouts → ApiResult<IraContributionPhaseout[]>
 *              (V3ConfigFunction.cs Config_IraContributionPhaseouts — filters IsActive,
 *               camelCases every column; decimals serialized as strings).
 *   - Write:   POST /v3/admin/ira-contribution-phaseouts (V3AdminFunction.cs
 *              Admin_IraContributionPhaseouts_Upsert — persists EVERY field:
 *              taxYear/planCode/filingStatus/magiPhaseoutStart/magiPhaseoutEnd/
 *              notes/isActive; no silent drop. plan/filing upper-cased server-side).
 *   - Delete:  DELETE /v3/admin/ira-contribution-phaseouts/{taxYear}/{planCode}/{filingStatus}
 *              is a HARD delete. Because the entity carries IsActive and these
 *              rows are IRS history, this UI SOFT-deletes via re-upsert
 *              isActive=false (the hard-delete route stays available but unused).
 *
 * Typed API: client.retail.config.iraContributionPhaseouts() (read);
 *            client.retail.admin.iraContributionPhaseouts.upsert(sentinel, body) (write).
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type IraContributionPhaseoutUpsertBody,
} from '@retail-ops/api-client';
import type { IraContributionPhaseout } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField, BoolField, TextArea } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface PhaseoutRow {
  taxYear: number;
  planCode: string;
  filingStatus: string;
  /** decimal carried as string to preserve precision over the wire. */
  magiPhaseoutStart: string;
  magiPhaseoutEnd: string;
  notes: string | null;
  isActive: boolean;
}

interface EditDraft extends PhaseoutRow {
  isNew: boolean;
}

const PLAN_CODES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'T', label: 'Traditional IRA' },
  { code: 'R', label: 'Roth IRA' },
];

const FILING_STATUSES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'SINGLE', label: 'Single' },
  { code: 'MFJ', label: 'Married filing jointly' },
  { code: 'MFS', label: 'Married filing separately' },
  { code: 'HOH', label: 'Head of household' },
  { code: 'QW', label: 'Qualifying widow(er)' },
];

const PLAN_OPTIONS = PLAN_CODES.map((p) => ({ value: p.code, label: p.label }));
const FILING_OPTIONS = FILING_STATUSES.map((f) => ({ value: f.code, label: f.label }));

function planLabel(code: string): string {
  return PLAN_CODES.find((p) => p.code === code)?.label ?? code;
}

function filingLabel(code: string): string {
  return FILING_STATUSES.find((f) => f.code === code)?.label ?? code;
}

function fmtMoney(s: string | null | undefined): string {
  if (s == null || s === '') return '—';
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function emptyDraft(preselectYear?: number): EditDraft {
  return {
    taxYear: preselectYear ?? new Date().getFullYear(),
    planCode: 'T',
    filingStatus: 'SINGLE',
    magiPhaseoutStart: '0',
    magiPhaseoutEnd: '0',
    notes: '',
    isActive: true,
    isNew: true,
  };
}

function rowToDraft(r: PhaseoutRow): EditDraft {
  return { ...r, notes: r.notes ?? '', isNew: false };
}

function draftToUpsertBody(d: EditDraft): IraContributionPhaseoutUpsertBody {
  return {
    taxYear: d.taxYear,
    planCode: d.planCode,
    filingStatus: d.filingStatus,
    magiPhaseoutStart: d.magiPhaseoutStart,
    magiPhaseoutEnd: d.magiPhaseoutEnd,
    notes: d.notes && d.notes.trim() ? d.notes.trim() : null,
    isActive: d.isActive,
  };
}

interface YearGroup {
  year: number;
  rows: ReadonlyArray<PhaseoutRow>;
}

function groupByYear(rows: ReadonlyArray<PhaseoutRow>): ReadonlyArray<YearGroup> {
  const map = new Map<number, PhaseoutRow[]>();
  for (const r of rows) {
    const list = map.get(r.taxYear) ?? [];
    list.push(r);
    map.set(r.taxYear, list);
  }
  return Array.from(map.entries())
    .map(([year, list]) => ({
      year,
      rows: list
        .slice()
        .sort(
          (a, b) =>
            a.planCode.localeCompare(b.planCode) || a.filingStatus.localeCompare(b.filingStatus),
        ),
    }))
    .sort((a, b) => b.year - a.year);
}

export function IraContributionPhaseoutsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<PhaseoutRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PhaseoutRow | null>(null);
  const [rollForwardYear, setRollForwardYear] = useState<number | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.config.iraContributionPhaseouts();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          const mapped: PhaseoutRow[] = resp.data.map((r: IraContributionPhaseout) => ({
            taxYear: r.taxYear,
            planCode: r.planCode,
            filingStatus: r.filingStatus,
            magiPhaseoutStart: r.magiPhaseoutStart,
            magiPhaseoutEnd: r.magiPhaseoutEnd,
            notes: r.notes ?? null,
            isActive: r.isActive,
          }));
          setRows(mapped);
        } else {
          setLoadError(resp.error ?? 'Failed to load IRA contribution phaseouts.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const groups = useMemo(() => groupByYear(rows ?? []), [rows]);
  const latestYear = useMemo(() => groups[0]?.year ?? null, [groups]);

  const upsert = useCallback(
    async (d: EditDraft): Promise<boolean> => {
      const resp = await client.retail.admin.iraContributionPhaseouts.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(d),
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!Number.isInteger(draft.taxYear) || draft.taxYear < 1990 || draft.taxYear > 2100) {
      setSaveError('Tax year must be a 4-digit year between 1990 and 2100.');
      return;
    }
    if (!PLAN_CODES.some((p) => p.code === draft.planCode)) {
      setSaveError('Pick a plan.');
      return;
    }
    if (!FILING_STATUSES.some((f) => f.code === draft.filingStatus)) {
      setSaveError('Pick a filing status.');
      return;
    }
    const start = Number(draft.magiPhaseoutStart);
    const end = Number(draft.magiPhaseoutEnd);
    if (!Number.isFinite(start) || start < 0) {
      setSaveError('Phaseout start must be a non-negative number.');
      return;
    }
    if (!Number.isFinite(end) || end < 0) {
      setSaveError('Phaseout end must be a non-negative number.');
      return;
    }
    if (end < start) {
      setSaveError('Phaseout end cannot be lower than the start.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert(draft);
      setToast({
        tone: 'info',
        text: `Saved. ${draft.taxYear} ${planLabel(draft.planCode)} / ${filingLabel(draft.filingStatus)} updated.`,
      });
      setDraft(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Soft-delete: re-upsert with isActive=false so the IRS-history row is
      // kept for audit. (The hard-delete route exists but the UI never calls it.)
      await upsert({ ...deleteTarget, notes: deleteTarget.notes ?? '', isActive: false, isNew: false });
      setToast({
        tone: 'warn',
        text: `Deactivated ${deleteTarget.taxYear} ${planLabel(deleteTarget.planCode)} / ${filingLabel(deleteTarget.filingStatus)}. The runtime now skips it.`,
      });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, upsert, refresh]);

  const confirmRollForward = useCallback(async () => {
    if (latestYear == null) return;
    const newYear = latestYear + 1;
    if (groups.some((g) => g.year === newYear)) {
      setToast({ tone: 'warn', text: `${newYear} already exists. Edit it directly instead.` });
      setRollForwardYear(null);
      return;
    }
    const sourceGroup = groups.find((g) => g.year === latestYear);
    if (!sourceGroup) return;
    setSaving(true);
    setSaveError(null);
    let okCount = 0;
    let failCount = 0;
    for (const r of sourceGroup.rows) {
      try {
        await upsert({
          ...r,
          taxYear: newYear,
          // Leave dollar fields blank (zero) for admin entry.
          magiPhaseoutStart: '0',
          magiPhaseoutEnd: '0',
          notes: 'Rolled forward — admin enters IRS-published bracket numbers.',
          isActive: true,
          isNew: true,
        });
        okCount += 1;
      } catch {
        failCount += 1;
      }
    }
    setSaving(false);
    setRollForwardYear(null);
    refresh();
    if (failCount === 0) {
      setToast({
        tone: 'info',
        text: `Created ${okCount} blank ${newYear} rows. Fill in the IRS-published amounts and save each.`,
      });
    } else {
      setToast({
        tone: 'warn',
        text: `Roll-forward partial: ${okCount} created, ${failCount} failed. Re-run after fixing.`,
      });
    }
  }, [groups, latestYear, upsert, refresh]);

  const columns: ReadonlyArray<DataTableColumn<PhaseoutRow>> = useMemo(
    () => [
      { header: 'Plan', cell: (r) => <span className="cell-strong">{planLabel(r.planCode)}</span> },
      { header: 'Filing status', cell: (r) => filingLabel(r.filingStatus) },
      {
        header: 'Phaseout start',
        align: 'right',
        cell: (r) => <span className="cell-mono num">{fmtMoney(r.magiPhaseoutStart)}</span>,
      },
      {
        header: 'Phaseout end',
        align: 'right',
        cell: (r) => <span className="cell-mono num">{fmtMoney(r.magiPhaseoutEnd)}</span>,
      },
      { header: 'Notes', cell: (r) => <span className="cell-desc">{r.notes ?? '—'}</span> },
      {
        header: 'Active',
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft(rowToDraft(r)); setSaveError(null); }}>Edit</Button>
            {r.isActive && <Button onClick={() => setDeleteTarget(r)}>Deactivate</Button>}
          </div>
        ),
      },
    ],
    [],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load IRA contribution phaseouts: {loadError}{' '}
        <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading IRA contribution phaseouts…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        Source of truth: IRS Publication 590-A. Adjust the start and end dollar values when the IRS
        publishes inflation-adjusted brackets each fall. Rolling forward creates blank rows for the
        next year so you can enter the new figures without re-typing every (plan, filing status)
        combination.
      </Banner>

      <Card
        title={`IRA Contribution Phaseouts — ${rows.length} bracket${rows.length === 1 ? '' : 's'} across ${groups.length} year${groups.length === 1 ? '' : 's'}`}
        actions={
          <div className="card-actions-row">
            {latestYear != null && (
              <Button
                onClick={() => setRollForwardYear(latestYear + 1)}
                disabled={saving}
              >
                Roll forward to {latestYear + 1}
              </Button>
            )}
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Add Bracket
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          MAGI phaseout brackets per (Tax Year × Plan × Filing Status). Drives Traditional IRA
          deductibility caps and Roth IRA eligibility caps. Grouped by tax year, newest first.
        </p>
      </Card>

      {groups.length === 0 ? (
        <Card title="No brackets yet">
          <p className="empty">
            No IRA contribution phaseout brackets are configured.{' '}
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Add the first bracket
            </Button>
          </p>
        </Card>
      ) : (
        groups.map((g) => (
          <Card
            key={g.year}
            title={`Tax year ${g.year} — ${g.rows.length} bracket${g.rows.length === 1 ? '' : 's'}`}
            actions={
              <Button onClick={() => { setDraft(emptyDraft(g.year)); setSaveError(null); }}>
                + Bracket in {g.year}
              </Button>
            }
          >
            <DataTable<PhaseoutRow>
              columns={columns}
              rows={g.rows}
              rowKey={(r) => `${r.taxYear}-${r.planCode}-${r.filingStatus}`}
              emptyState={<p className="empty">No brackets for {g.year}.</p>}
            />
          </Card>
        ))
      )}

      <ModalShell
        open={draft !== null}
        title={
          draft?.isNew
            ? 'Add a contribution phaseout bracket'
            : `Edit ${draft?.taxYear ?? ''} ${draft ? planLabel(draft.planCode) : ''} / ${draft ? filingLabel(draft.filingStatus) : ''}`
        }
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={640}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <NumberField
                label="Tax year"
                required
                value={draft.taxYear}
                onChange={(v) => draft.isNew && setDraft({ ...draft, taxYear: v ?? draft.taxYear })}
                min={1990}
                max={2100}
                disabled={!draft.isNew}
                hint={
                  draft.isNew
                    ? 'Four-digit year. Permanent once saved — delete + re-create to change.'
                    : 'Permanent — tax year cannot change after creation.'
                }
              />
              <SelectField
                label="Plan"
                required
                value={draft.planCode}
                onChange={(v) => draft.isNew && setDraft({ ...draft, planCode: v })}
                options={PLAN_OPTIONS}
                disabled={!draft.isNew}
                hint={draft.isNew ? 'Permanent.' : 'Permanent — cannot change.'}
              />
            </div>

            <SelectField
              label="Filing status"
              required
              value={draft.filingStatus}
              onChange={(v) => draft.isNew && setDraft({ ...draft, filingStatus: v })}
              options={FILING_OPTIONS}
              disabled={!draft.isNew}
              hint={draft.isNew ? 'Permanent.' : 'Permanent — cannot change.'}
            />

            <div className="form-row">
              <TextField
                label="Phaseout start ($)"
                required
                value={draft.magiPhaseoutStart}
                onChange={(v) => setDraft({ ...draft, magiPhaseoutStart: v })}
                monospace
                placeholder="e.g. 150000"
                hint="Lower bound of the MAGI phaseout band. Full eligibility below this amount."
              />
              <TextField
                label="Phaseout end ($)"
                required
                value={draft.magiPhaseoutEnd}
                onChange={(v) => setDraft({ ...draft, magiPhaseoutEnd: v })}
                monospace
                placeholder="e.g. 165000"
                hint="Upper bound of the MAGI phaseout band. Eligibility zero at or above this amount."
              />
            </div>

            <TextArea
              label="Notes"
              value={draft.notes ?? ''}
              onChange={(v) => setDraft({ ...draft, notes: v })}
              rows={3}
              placeholder="e.g. IRS Pub 590-A 2026 — Roth eligibility (single)."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive rows are skipped at runtime."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Deactivate this bracket?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleSoftDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Mark inactive'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We&apos;ll mark the <strong>{deleteTarget.taxYear}</strong>{' '}
              {planLabel(deleteTarget.planCode)} / {filingLabel(deleteTarget.filingStatus)} bracket
              inactive. The runtime skips it, but the IRS-history row stays for audit — flip it back
              on anytime by editing it.
            </p>
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={rollForwardYear !== null && latestYear !== null}
        title={`Roll forward to ${rollForwardYear ?? ''}?`}
        onClose={() => {
          if (saving) return;
          setRollForwardYear(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          <>
            <Button onClick={() => setRollForwardYear(null)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void confirmRollForward()} disabled={saving}>
              {saving ? 'Working…' : `Create ${rollForwardYear ?? ''} rows`}
            </Button>
          </>
        }
      >
        <div className="form-stack">
          {saveError && <Banner tone="err">{saveError}</Banner>}
          <p className="modal-prose">
            We&apos;ll copy every (plan, filing status) row from <strong>{latestYear}</strong> into{' '}
            <strong>{rollForwardYear}</strong>, leaving the dollar amounts at $0. Edit each row with
            the IRS-published numbers when they post. This step is reversible — deactivate any row
            you don&apos;t want.
          </p>
        </div>
      </ModalShell>
    </>
  );
}

export default IraContributionPhaseoutsPage;
